/**
 * SharePlanPickerSheet — "which plan should this shared file go to?"
 *
 * This replaces an ActionSheetIOS call, and the reason is not style.
 * `ActionSheetIOS` is exported by react-native on EVERY platform — index.js
 * has no platform split — so a truthiness check on it passes on Android and
 * then `showActionSheetWithOptions` throws
 * `invariant(RCTActionSheetManager, "ActionSheetManager doesn't exist")`,
 * because the native module is iOS-only. Thrown from inside a useEffect in a
 * component mounted outside the navigation ErrorBoundary, that crashes the
 * whole app to the root fallback. Feature-detection cannot save it; only not
 * calling it can.
 *
 * react-native-web does not export ActionSheetIOS at all, which is why the old
 * code reached for it through a runtime `require`. A rendered sheet needs no
 * such trick and behaves the same on all three platforms.
 */

import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Pressable,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';

export interface SharePlanOption {
  id: number | string;
  label: string;
}

interface Props {
  visible: boolean;
  options: SharePlanOption[];
  /** Short description of what is being imported, e.g. "2 files". */
  subtitle?: string;
  onSelect: (option: SharePlanOption) => void;
  onCancel: () => void;
}

export default function SharePlanPickerSheet({ visible, options, subtitle, onSelect, onCancel }: Props) {
  const theme = useTheme();
  const sheet = useSheetLayout({ desktopMaxWidth: 460 });
  const styles = makeStyles(theme);
  const { height: windowHeight } = useWindowDimensions();

  const listHeight = Number.isFinite(windowHeight) && windowHeight > 0
    ? Math.max(180, windowHeight * 0.5)
    : 360;

  return (
    <Modal
      visible={visible}
      transparent
      animationType={sheet.animationType}
      // Android's hardware back must cancel rather than leave the sheet up with
      // no way out — onRequestClose is the only thing that receives that press.
      onRequestClose={onCancel}
      statusBarTranslucent
    >
      <Pressable style={[styles.overlay, sheet.overlayStyle]} onPress={onCancel}>
        <Pressable style={[styles.sheet, { backgroundColor: theme.colors.card }, sheet.sheetStyle]} onPress={() => {}}>
          {sheet.showHandle && <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />}

          <View style={styles.header}>
            <Text style={[styles.title, { color: theme.colors.text }]}>Add to which plan?</Text>
            <Text style={[styles.subtitle, { color: theme.colors.muted }]}>
              {subtitle || 'Choose where this shared content should be saved.'}
            </Text>
          </View>

          <ScrollView style={{ maxHeight: listHeight }} keyboardShouldPersistTaps="handled">
            {options.map((option) => (
              <TouchableOpacity
                key={String(option.id)}
                testID={`share-plan-option-${option.id}`}
                style={[styles.row, { borderColor: theme.colors.border }]}
                onPress={() => onSelect(option)}
                accessibilityRole="button"
                accessibilityLabel={`Import to ${option.label}`}
              >
                <Text style={[styles.rowLabel, { color: theme.colors.text }]} numberOfLines={1}>{option.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <TouchableOpacity
            testID="share-plan-cancel"
            style={[styles.cancelRow, { borderTopColor: theme.colors.border }]}
            onPress={onCancel}
            accessibilityRole="button"
          >
            <Text style={[styles.cancelText, { color: theme.colors.muted }]}>Cancel</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const makeStyles = (theme: ReturnType<typeof import('../hooks/useTheme').useTheme>) =>
  StyleSheet.create({
    overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
    sheet: { borderTopLeftRadius: 22, borderTopRightRadius: 22, paddingBottom: 6 },
    handle: { width: 36, height: 4, borderRadius: 2, alignSelf: 'center', marginTop: 10 },
    header: { paddingHorizontal: 20, paddingTop: 14, paddingBottom: 10 },
    title: { fontSize: 18, fontWeight: '700' },
    subtitle: { fontSize: 13, marginTop: 2 },
    row: {
      paddingHorizontal: 20,
      paddingVertical: 14,
      borderBottomWidth: StyleSheet.hairlineWidth,
    },
    rowLabel: { fontSize: 15 },
    cancelRow: { alignItems: 'center', paddingVertical: 14, borderTopWidth: StyleSheet.hairlineWidth },
    cancelText: { fontSize: 14, fontWeight: '600' },
  });
